import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  HelpCircle, 
  Search, 
  MessageCircle,
  ArrowLeft,
  BookOpen,
  Video,
  FileText,
  Phone,
  Mail,
  ChevronRight,
  ExternalLink
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'

interface HelpArticle {
  id: string
  title: string
  category: string
  content: string
  tags: string[]
  lastUpdated: string
}

interface FAQ {
  id: string
  question: string
  answer: string
  category: string
}

const helpCategories = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'Learn the basics of using the platform',
    icon: BookOpen,
    color: 'bg-blue-100 text-blue-600'
  },
  {
    id: 'courses',
    name: 'Courses & Learning',
    description: 'Everything about courses and assignments',
    icon: BookOpen,
    color: 'bg-green-100 text-green-600'
  },
  {
    id: 'account',
    name: 'Account & Profile',
    description: 'Manage your account and profile settings',
    icon: HelpCircle,
    color: 'bg-purple-100 text-purple-600'
  },
  {
    id: 'technical',
    name: 'Technical Support',
    description: 'Troubleshooting and technical issues',
    icon: FileText,
    color: 'bg-orange-100 text-orange-600'
  }
]

const faqs: FAQ[] = [
  {
    id: '1',
    question: 'How do I enroll in a course?',
    answer: 'To enroll in a course, navigate to the Courses page, find the course you want to take, and click the "Enroll" button. You\'ll be automatically enrolled and can start learning immediately.',
    category: 'courses'
  },
  {
    id: '2',
    question: 'How do I submit an assignment?',
    answer: 'Go to the Assignments page, find the assignment you want to submit, click "Submit Assignment", upload your files, and add any required text. Click "Submit" when you\'re done.',
    category: 'courses'
  },
  {
    id: '3',
    question: 'How do I change my password?',
    answer: 'Go to More Options > Security Settings, enter your current password, then enter your new password twice. Click "Change Password" to save the changes.',
    category: 'account'
  },
  {
    id: '4',
    question: 'Why can\'t I access a course?',
    answer: 'Make sure you\'re enrolled in the course and that it\'s not restricted to certain user roles. If you\'re still having issues, contact support.',
    category: 'technical'
  },
  {
    id: '5',
    question: 'How do I download my certificates?',
    answer: 'Go to the Certificates page to view all your earned certificates. Click the download button next to any certificate to save it to your device.',
    category: 'courses'
  }
]

const helpArticles: HelpArticle[] = [
  {
    id: '1',
    title: 'Complete Guide to Course Navigation',
    category: 'getting-started',
    content: 'Learn how to navigate through courses, access materials, and track your progress...',
    tags: ['navigation', 'courses', 'basics'],
    lastUpdated: '2024-01-15'
  },
  {
    id: '2',
    title: 'How to Submit Assignments',
    category: 'courses',
    content: 'Step-by-step guide on submitting assignments, including file uploads and formatting...',
    tags: ['assignments', 'submission', 'files'],
    lastUpdated: '2024-01-14'
  },
  {
    id: '3',
    title: 'Managing Your Profile Settings',
    category: 'account',
    content: 'Learn how to update your personal information, change preferences, and manage privacy settings...',
    tags: ['profile', 'settings', 'privacy'],
    lastUpdated: '2024-01-13'
  }
]

export function HelpPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null)

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !selectedCategory || faq.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const filteredArticles = helpArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesCategory = !selectedCategory || article.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const toggleFAQ = (faqId: string) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/more')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Help Center</h1>
            <p className="text-gray-600">Get help and support</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card className="p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search for help articles, FAQs, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button className="bg-primary-600 hover:bg-primary-700">
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <MessageCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Live Chat</h3>
              <p className="text-sm text-gray-600">Chat with our support team</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <Phone className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Phone Support</h3>
              <p className="text-sm text-gray-600">Call us at +1 (555) 123-4567</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Mail className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Email Support</h3>
              <p className="text-sm text-gray-600">Send us an email</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Categories */}
        <div className="lg:col-span-1">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Help Categories</h3>
            <div className="space-y-3">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  !selectedCategory
                    ? 'bg-primary-50 border border-primary-200'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">All Categories</div>
                <div className="text-sm text-gray-600">View all help content</div>
              </button>
              {helpCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-primary-50 border border-primary-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${category.color}`}>
                      <category.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{category.name}</div>
                      <div className="text-sm text-gray-600">{category.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* FAQs */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
            <div className="space-y-3">
              {filteredFAQs.map((faq) => (
                <div key={faq.id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">{faq.question}</h4>
                      <ChevronRight 
                        className={`h-5 w-5 text-gray-400 transition-transform ${
                          expandedFAQ === faq.id ? 'rotate-90' : ''
                        }`} 
                      />
                    </div>
                  </button>
                  {expandedFAQ === faq.id && (
                    <div className="px-4 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Help Articles */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Help Articles</h3>
            <div className="space-y-4">
              {filteredArticles.map((article) => (
                <div key={article.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-2">{article.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{article.content}</p>
                      <div className="flex items-center space-x-4">
                        <div className="flex space-x-2">
                          {article.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <span className="text-xs text-gray-500">
                          Updated {new Date(article.lastUpdated).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Read
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Contact Support */}
      <Card className="p-6 bg-primary-50 border-primary-200">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">Still need help?</h3>
          <p className="text-primary-700 mb-4">
            Our support team is here to help you with any questions or issues.
          </p>
          <div className="flex justify-center space-x-4">
            <Button className="bg-primary-600 hover:bg-primary-700">
              <MessageCircle className="h-4 w-4 mr-2" />
              Start Live Chat
            </Button>
            <Button variant="outline" className="border-primary-300 text-primary-700 hover:bg-primary-100">
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
